import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { PublicShell } from '@/components/layout/PublicShell';
import { useBranding, usePlatformName } from '@/hooks/useBranding';

type FaqItem = { q: string; a: React.ReactNode };
type FaqGroup = { title: string; items: FaqItem[] };

export function FAQPage() {
  const name = usePlatformName();
  const { data } = useBranding();
  const supportEmail = data?.branding.support_email || 'bot@workercms.com';

  const groups: FaqGroup[] = [
    {
      title: 'Genel',
      items: [
        {
          q: `${name} nedir?`,
          a: (
            <>
              {name}, yapay zeka ile blog ve makale içeriği üreten, üretilen içerikleri
              WordPress sitelerine otomatik yayınlayan bir SaaS platformudur. Tek panelden
              birden fazla siteyi yönetebilir, ekip arkadaşlarınla ortak çalışabilirsin.
            </>
          ),
        },
        {
          q: 'Kimler için uygun?',
          a: (
            <>
              İçerik pazarlaması yapan markalar, SEO ajansları, affiliate siteler, haber &
              blog yayıncıları, WordPress üzerinde çalışan freelance editörler için uygundur.
            </>
          ),
        },
        {
          q: 'Üye olmak için kredi kartı gerekiyor mu?',
          a: (
            <>
              Hayır. E-posta ile kayıt olup 7 günlük deneme kredisi ile başlayabilirsin.
              Beğenirsen kripto ile bakiye yüklersin.
            </>
          ),
        },
      ],
    },
    {
      title: 'Kredi & Ödeme',
      items: [
        {
          q: 'Kredi nedir, nasıl çalışır?',
          a: (
            <>
              Her işlem (makale üretimi, görsel üretimi vb.) belirli bir kredi düşer. 1 kredi
              = 0.000001 USD (µUSD). Örneğin GPT-5 nano ile 1000 kelime üretmek, modelin
              token fiyatına göre yaklaşık 1–2 kredi düşürür. Bakiyende ne kadar kalırsa o
              kadar kullanabilirsin — abonelik yok.
            </>
          ),
        },
        {
          q: 'Hangi ödeme yöntemleri destekleniyor?',
          a: (
            <>
              Şu an yalnızca <strong>kripto ödeme</strong> (USDT, USDC vb.) destekleniyor.
              Desteklenen ağlar: Ethereum, BSC, Polygon, Arbitrum, Optimism, Base, Tron,
              Solana, TON. Ödeme blockchain explorer'dan otomatik doğrulanır.
            </>
          ),
        },
        {
          q: 'Kredim biterse ne olur?',
          a: (
            <>
              İçerik üretimi duraklar. Hesabın açık kalır, verilerin korunur. Yeni bakiye
              yüklediğinde kaldığın yerden devam edersin.
            </>
          ),
        },
        {
          q: 'İade mümkün mü?',
          a: (
            <>
              Kripto ödemeler blockchain üzerinde geri dönüşsüzdür; bu nedenle yüklenen
              bakiye iade edilmez. Ancak Platform kaynaklı teknik hata nedeniyle kredi
              tüketilmiş ise destek talebinizle iade yapılır. Detaylar:{' '}
              <Link to="/terms" className="text-primary hover:underline">Kullanım Şartları</Link>.
            </>
          ),
        },
      ],
    },
    {
      title: 'İçerik Üretimi',
      items: [
        {
          q: 'Hangi yapay zeka modelleri kullanılıyor?',
          a: (
            <>
              OpenAI (GPT-5, GPT-4.1), Anthropic Claude (Opus 4.7, Sonnet 4.6, Haiku 4.5),
              Google Gemini (2.5 Pro, 2.5 Flash), Perplexity, MiniMax, Kimi, GLM, Ernie
              gibi 80+ sohbet modeli ve Flux, DALL·E, Imagen, Gemini Image (Nano Banana),
              Stable Diffusion, Seedream, Recraft, Reve, Kling gibi 50+ görsel modeli
              tercih edebilirsin.
            </>
          ),
        },
        {
          q: 'İçerik şablonları neler?',
          a: (
            <>
              Nasıl Yapılır rehberleri, liste makaleleri, ürün incelemeleri, karşılaştırma
              yazıları, haber özetleri, URL rewrite, tarif, seyahat rehberi, sağlık &
              wellness, finans analizi, spor maç analizi, burç yorumu, alışveriş rehberi
              ve daha fazlası — 40+ şablon mevcut.
            </>
          ),
        },
        {
          q: 'Türkçe içerik ne kadar iyi?',
          a: (
            <>
              Seçtiğin modele göre değişir. Claude ve Gemini 2.5 Pro Türkçe doğal sonuçlar
              verir. GPT-5 ve Claude Opus akademik metinler için iyidir. Haiku ve Flash
              modelleri hızlı ve ekonomik; günlük blog içerikleri için ideal.
            </>
          ),
        },
        {
          q: 'Ürettiğim içerik benzersiz mi?',
          a: (
            <>
              Yapay zeka her seferinde yeni metin üretir, ancak %100 benzersizlik
              garantisi yoktur. Yayınlamadan önce bir plagiarism checker ile kontrol etmeni
              öneririz. Arama motorları AI içeriği tespit edebilir; insan dokunuşuyla
              zenginleştirmek faydalı olur.
            </>
          ),
        },
        {
          q: 'Toplu üretim var mı?',
          a: (
            <>
              Evet. <Link to="/content/bulk" className="text-primary hover:underline">Toplu Oluştur</Link>{' '}
              ekranından anahtar kelime listesi vererek onlarca içeriği tek seferde
              üretebilirsin. Listedeki içerikleri topluca siteye de gönderebilirsin.
            </>
          ),
        },
      ],
    },
    {
      title: 'WordPress & Yayın',
      items: [
        {
          q: 'WordPress nasıl bağlanır?',
          a: (
            <>
              Sites sayfasından "Yeni Site" ile WordPress URL'ni ve bir Uygulama Şifresi
              (Application Password) girersin. Şifre Platform'da AES-GCM ile şifreli
              saklanır. Gutenberg block editor uyumlu yayınlama yapılır.
            </>
          ),
        },
        {
          q: 'Kaç site bağlayabilirim?',
          a: (
            <>
              Varsayılan olarak 1 site bağlayabilirsin. "Ek Site" eklentisini satın alarak
              site sayısını artırabilirsin.
            </>
          ),
        },
        {
          q: 'Kategori ve etiket seçebilir miyim?',
          a: (
            <>
              Evet. İçerik gönderimi sırasında WordPress'teki mevcut kategori ve etiketler
              listelenir; istediğini seçersin. Seçmezsen WP'nin varsayılan kategorisine
              (Uncategorized) gider.
            </>
          ),
        },
        {
          q: 'Yayınlanmış içeriği güncelleyebilir miyim?',
          a: (
            <>
              Evet. İçerik kartındaki "Siteye Gönder" ile aynı siteye tekrar gönderirsen
              mevcut post güncellenir. Farklı siteye gönderirsen yeni bir post oluşur.
            </>
          ),
        },
      ],
    },
    {
      title: 'Görseller',
      items: [
        {
          q: 'Öne çıkan görsel otomatik üretiliyor mu?',
          a: (
            <>
              Evet. İstersen içerik oluşturma sırasında, istersen daha sonra İçerik Detay
              sayfasından üretebilirsin. Varsayılan model Gemini 2.5 Flash Image (Nano
              Banana) — hızlı ve ucuz. DALL·E 3, Imagen 4, Flux, Seedream gibi 50+ model
              arasından seçim yapabilirsin.
            </>
          ),
        },
        {
          q: 'Gövde içine görsel ekleyebilir miyim?',
          a: (
            <>
              Evet. Yazının gövdesine yerleştirilecek görseller için ayrı panel var. Her
              görselin yerleşim biçimini (tam genişlik, sol, sağ, dönüşümlü) seçebilirsin.
              Beğenmediğin bir görseli tek tuşla yeniden üretebilirsin.
            </>
          ),
        },
      ],
    },
    {
      title: 'Ekip & Çoklu Site',
      items: [
        {
          q: 'Ekip arkadaşlarımı davet edebilir miyim?',
          a: (
            <>
              Evet. Takım sayfasından e-posta ile editör davet edebilirsin. Editörler
              yalnızca içerik üretimi ve yönetimi yapar; ayarlara erişemez.
            </>
          ),
        },
        {
          q: 'Rol sistemi nasıl çalışıyor?',
          a: (
            <>
              <strong>Owner</strong> (Platform seviyesi, yalnızca sen), <strong>Admin</strong> (Organizasyon yöneticisi),
              {' '}<strong>Editor</strong> (içerik odaklı) rolleri var.
            </>
          ),
        },
      ],
    },
    {
      title: 'Güvenlik & Gizlilik',
      items: [
        {
          q: 'Verilerim ne kadar güvende?',
          a: (
            <>
              Şifreler bcrypt ile, API anahtarları ve WordPress kimlik bilgileri AES-GCM
              ile şifreli saklanır. Tüm trafik HTTPS üzerinden aktarılır. Altyapı
              Cloudflare üzerinde çalışır. Detaylar:{' '}
              <Link to="/privacy" className="text-primary hover:underline">Gizlilik Politikası</Link>.
            </>
          ),
        },
        {
          q: 'Kendi API anahtarımı (BYOK) kullanabilir miyim?',
          a: (
            <>
              Evet. "Bring Your Own Key" eklentisini satın alıp kendi OpenAI / Anthropic
              / Google anahtarlarını tanımlayabilirsin. Bu durumda içerik üretimi kendi
              hesabından faturalanır; platform sadece hizmet bedelini keser.
            </>
          ),
        },
        {
          q: 'Hesabımı silebilir miyim?',
          a: (
            <>
              Evet. Hesap ayarlarından hesabını kapatabilirsin. Ödeme ve yasal kayıtlar
              dışındaki veriler 90 gün içinde silinir.
            </>
          ),
        },
      ],
    },
    {
      title: 'Destek',
      items: [
        {
          q: 'Sorunum olursa nasıl iletişime geçebilirim?',
          a: (
            <>
              E-posta:{' '}
              <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
              . Genellikle 24 saat içinde yanıt veririz.
            </>
          ),
        },
        {
          q: 'Özellik talebi / öneri iletebilir miyim?',
          a: (
            <>
              Memnun oluruz. E-posta ile ulaşabilir, kullanım senaryonu detaylandırırsan
              yol haritamıza alabiliriz.
            </>
          ),
        },
      ],
    },
  ];

  return (
    <PublicShell>
      <article className="container max-w-3xl py-12 space-y-8">
        <header className="text-center space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">Sıkça Sorulan Sorular</h1>
          <p className="text-muted-foreground">
            Aradığını bulamazsan{' '}
            <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
            {' '}adresinden ulaşabilirsin.
          </p>
        </header>

        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.title} className="space-y-2">
              <h2 className="text-xl font-semibold">{g.title}</h2>
              <div className="divide-y rounded-lg border">
                {g.items.map((item, i) => (
                  <FaqRow key={i} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="text-center pt-4 border-t text-sm text-muted-foreground space-x-4">
          <Link to="/privacy" className="hover:text-foreground hover:underline">Gizlilik</Link>
          <Link to="/terms" className="hover:text-foreground hover:underline">Şartlar</Link>
          <Link to="/aup" className="hover:text-foreground hover:underline">AUP</Link>
          <Link to="/pricing" className="hover:text-foreground hover:underline">Paketler</Link>
        </footer>
      </article>
    </PublicShell>
  );
}

function FaqRow({ item }: { item: FaqItem }) {
  return (
    <details className="group">
      <summary className="flex items-start gap-3 px-4 py-3 cursor-pointer list-none select-none hover:bg-accent/40">
        <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        <span className="font-medium text-sm">{item.q}</span>
      </summary>
      <div className="px-4 pb-4 pl-11 text-sm text-muted-foreground leading-relaxed">
        {item.a}
      </div>
    </details>
  );
}
